import React, { useContext } from "react";
import { INCENTIVE_PERMISSION_KEYS, OUTCOME_INCENTIVE_STATUS } from "@/other/constants/IncentiveKeys.jsx";
import { DateHelper } from "@bryntum/schedulerpro";
import { INCENTIVE_FILTER_OPERATOR_VALUES, RESOURCE_FILTER_FIELD_OPTIONS, TASK_FILTER_FIELD_OPTIONS, INCENTIVE_FILTER_ENTITY_TYPES } from "@/other/constants/IncentiveKeys.jsx";
import { checkIncentivePermission, getIncentiveFilterOptions } from "../../../../../other/utils/incentiveUtils.jsx";
import { Info } from "lucide-react";
import { Calendar } from "lucide-react";
import { Switch } from "@/modern/components/ui/switch";
import { List } from "lucide-react";
import { pluralize } from "@/other/utils/otherUtils.jsx";
import { cn } from "@/modern/utils";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { BaseModal } from "../../base/BaseModal.jsx";
import { BaseModalBody } from "../../base/BaseModalBody.jsx";

const formatDate = (dateString) => {
  if (!dateString) return "Not set";
  return DateHelper.format(new Date(dateString), "MMM D, YYYY");
};

const getDurationText = (record) => {
  const now = new Date();
  const startDate = record.startDate ? new Date(record.startDate) : null;
  const endDate = record.endDate ? new Date(record.endDate) : null;
  const isActive = record.status === OUTCOME_INCENTIVE_STATUS.ACTIVE;
  const isPaused = record.status === OUTCOME_INCENTIVE_STATUS.PAUSED;

  if (!startDate) {
    return "Not started";
  }

  // Both start and end date exist
  if (startDate && endDate) {
    const days = DateHelper.diff(startDate, endDate, "d") + 1;
    const displayDays = Math.floor(days); // Round down to lowest integer

    // Check if end date is in the future
    if (endDate > now) {
      return `Outcome point will run for ${displayDays} ${pluralize(displayDays, "day")}`;
    } else {
      return `Outcome point ran for ${displayDays} ${pluralize(displayDays, "day")}`;
    }
  }

  // Only start date exists
  if (startDate && !endDate) {
    const daysSinceStart = DateHelper.diff(startDate, now, "d");
    const displayDays = Math.floor(daysSinceStart); // Round down to lowest integer

    if (daysSinceStart < 0) {
      // Start date is in the future
      return "Not started yet";
    } else if (daysSinceStart === 0) {
      // Started today
      if (isActive) {
        return "Active since today";
      } else if (isPaused) {
        return "Started today";
      } else {
        return "Started today";
      }
    } else {
      // Started in the past
      if (isActive) {
        return `Active for ${displayDays} ${pluralize(displayDays, "day")}`;
      } else if (isPaused) {
        return `Started ${displayDays} ${pluralize(displayDays, "day")} ago`;
      } else {
        return `Started ${displayDays} ${pluralize(displayDays, "day")} ago`;
      }
    }
  }

  return "Duration unknown";
};

const formatFilters = (filters, getOptions, fieldOptions, project, entityType) => {
  if (!filters || filters.length === 0) return [];

  return filters.map((filter, index) => {
    const { field, operator, value, booleanOperator } = filter;
    const label = fieldOptions.find((option) => option.value === field)?.label;
    let formattedValue = getOptions(field, entityType, project).find((option) => option.value === value)?.label;

    let valueDisplay;
    if (!formattedValue) {
      valueDisplay = `<span title="The ${label.toLowerCase()} has been deleted" class="px-2 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 font-medium inline-flex items-center gap-1"><i class="fas fa-x text-xs"></i></span>`;
    } else {
      valueDisplay = formattedValue;
    }

    const formattedOperator = INCENTIVE_FILTER_OPERATOR_VALUES[operator];
    const formattedBooleanOperator = booleanOperator ? ` ${booleanOperator} ` : "";

    return {
      index,
      label,
      operator: formattedOperator,
      value: valueDisplay,
      booleanOperator: formattedBooleanOperator,
      entityType
    };
  });
};

const buildGroupText = (group) => {
  if (group.values.length === 1) {
    const operatorText = group.operator === "Is" ? "is" : "is not";
    return `${operatorText} ${group.values[0]}`;
  } else {
    // Check if all boolean operators in this group are the same
    const allSameOp = group.booleanOps.every((op) => op === group.booleanOps[0]);
    const operatorText = group.operator === "Is" ? "is" : "is not";

    if (allSameOp && group.booleanOps[0] === "OR") {
      return `${operatorText} either ${group.values.join(", ")}`;
    } else if (allSameOp && group.booleanOps[0] === "AND") {
      return `${operatorText} ${group.values.join(", ")}`;
    } else {
      // Mixed operators - fall back to individual format
      const parts = [];
      group.values.forEach((value, index) => {
        if (index === 0) {
          parts.push(`${operatorText} ${value}`);
        } else {
          const op = group.booleanOps[index - 1]?.toLowerCase() || "and";
          parts.push(`${op} ${operatorText} ${value}`);
        }
      });
      return parts.join(" ");
    }
  }
};

const findGroupEndIndex = (filters, groupIndex) => {
  let currentGroup = 0;
  let lastIndex = 0;

  for (let i = 0; i < filters.length; i++) {
    const filter = filters[i];
    const nextFilter = filters[i + 1];
    const shouldStartNewGroup = !nextFilter || nextFilter.operator !== filter.operator || nextFilter.booleanOperator?.trim() !== filter.booleanOperator?.trim();

    if (shouldStartNewGroup) {
      if (currentGroup === groupIndex) {
        return i;
      }
      currentGroup++;
    }
  }

  return lastIndex;
};

const highlightValue = (text) => {
  // If text is already HTML (contains <span>), return as-is
  if (typeof text === "string" && text.includes("<span")) {
    return text;
  }
  // Otherwise, wrap in highlight styling
  return `<span class="px-2 py-0.5 rounded bg-[hsl(207_89%_68%_/_0.2)] text-[hsl(212_79%_42%)] dark:bg-[hsl(212_79%_42%_/_0.2)] dark:text-[hsl(207_89%_68%)] font-medium">${text}</span>`;
};

const boldField = (text) => {
  return `<span class="font-semibold">${text}</span>`;
};

const renderConditions = (record, pointRecord, project) => {
  const taskFilters = formatFilters(record.taskFilter, getIncentiveFilterOptions, TASK_FILTER_FIELD_OPTIONS, project, INCENTIVE_FILTER_ENTITY_TYPES.TASK);
  const resourceFilters = formatFilters(record.resourceFilter, getIncentiveFilterOptions, RESOURCE_FILTER_FIELD_OPTIONS, project, INCENTIVE_FILTER_ENTITY_TYPES.RESOURCE);

  const allFilters = [...taskFilters, ...resourceFilters];


  const conditionMessage = () => {
    return <p className="text-base font-medium m-0">
      {pointRecord ? (
        <>
          <span className="font-semibold text-foreground">{pointRecord.boardUserName || "N/A"}</span> was awarded{" "}
          <span className="font-bold text-md text-green-600 dark:text-green-400">{pointRecord.points || "N/A"} points</span> on <span className="font-medium">{pointRecord.dateEarned ? formatDate(pointRecord.dateEarned) : "N/A"}</span> for
          meeting these conditions:
        </>
      ) : (
        <>
          The point gives <span className={cn("font-bold text-lg ", record.points > 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400")}>{record.points || 0} {pluralize(record.points || 0, "point", "s")}</span> {record.multiplyByDays ? "per day" : ""} to {resourceFilters.length ? "a" : "all"} scheduled {pluralize(resourceFilters.length === 0 ? 2 : 1, "resource")} when:
        </>
      )}
    </p>
  }

  if (allFilters.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-base text-muted-foreground m-0">No filters applied - outcome point will apply to all resources</p>
      </div>
    );
  }

  // Group filters by field and entity type, preserving order and boolean operators
  const groupedFilters = allFilters.reduce((groups, filter) => {
    const key = `${filter.label}_${filter.entityType}`;
    if (!groups[key]) {
      groups[key] = {
        field: filter.label,
        entityType: filter.entityType,
        filters: []
      };
    }
    groups[key].filters.push(filter);
    return groups;
  }, {});

  const conditions = Object.values(groupedFilters).map((group) => {
    const isTask = group.entityType === INCENTIVE_FILTER_ENTITY_TYPES.TASK;
    const prefix = isTask ? "The task" : "The scheduled resource";
    const fieldName = group.field.toLowerCase();

    let condition = `${prefix} ${boldField(fieldName)}`;

    // Build condition by processing filters in order with proper grouping
    const parts = [];
    let currentGroup = { operator: null, values: [], booleanOps: [] };

    group.filters.forEach((filter, index) => {
      const value = highlightValue(filter.value);
      const booleanOp = filter.booleanOperator?.trim() || "AND";

      // Check if we should start a new group
      const shouldStartNewGroup = currentGroup.operator !== filter.operator || (currentGroup.booleanOps.length > 0 && currentGroup.booleanOps[currentGroup.booleanOps.length - 1] !== booleanOp);

      if (shouldStartNewGroup && currentGroup.operator !== null) {
        // Finalize current group
        const groupText = buildGroupText(currentGroup);
        parts.push(groupText);
        currentGroup = { operator: filter.operator, values: [], booleanOps: [] };
      }

      // Add to current group
      if (currentGroup.operator === null) {
        currentGroup.operator = filter.operator;
      }
      currentGroup.values.push(value);
      if (index > 0) {
        currentGroup.booleanOps.push(booleanOp);
      }
    });

    // Finalize the last group
    if (currentGroup.operator !== null) {
      const groupText = buildGroupText(currentGroup);
      parts.push(groupText);
    }

    // Connect parts with boolean operators
    if (parts.length === 1) {
      condition += ` ${parts[0]}.`;
    } else {
      let fullCondition = parts[0];
      for (let i = 1; i < parts.length; i++) {
        // Find the boolean operator that connects this part to the previous one
        const prevGroupEndIndex = findGroupEndIndex(group.filters, i - 1);
        const connectingOp = group.filters[prevGroupEndIndex]?.booleanOperator?.trim()?.toLowerCase() || "and";
        fullCondition += ` ${connectingOp} ${parts[i]}`;
      }
      condition += ` ${fullCondition}.`;
    }
    return condition;
  });

  return (
    <div className="space-y-3">
      {conditionMessage()}
      <ul className="space-y-2 ml-6 list-disc">
        {conditions.map((condition, index) => (
          <li key={index} className="text-base leading-relaxed text-muted-foreground">
            <span dangerouslySetInnerHTML={{ __html: condition }} />
          </li>
        ))}
      </ul>
    </div>
  );
};

export function OutcomeIncentivePreview(params) {
  const { record, pointRecord } = params;
  const { project } = useContext(BryntumContext);
  const isPaused = record.status === OUTCOME_INCENTIVE_STATUS.PAUSED;
  const isActive = record.status === OUTCOME_INCENTIVE_STATUS.ACTIVE;
  const isInactive = record.status === OUTCOME_INCENTIVE_STATUS.INACTIVE;

  const hasPermission = checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.INCENTIVE_MANAGERS });

  return (
    <BaseModal
      title={record.name || "N/A"}
      hideMenu={true}
      size={{
        width: "48rem",
        height: "auto",
        maxHeight: "90vh"
      }}
      className="!resize-none bg-background rounded-lg"
      headerClassName="py-4 px-6 min-h-auto"
      modal={false}
      {...params}
    >
      <BaseModalBody className="!p-6">
        <div className="flex items-center gap-2 rounded-lg bg-[hsl(207_89%_68%_/_0.2)] border border-[hsl(207_89%_68%_/_0.3)] px-6 py-4 text-[hsl(212_79%_42%)] dark:bg-[hsl(212_79%_42%_/_0.2)] dark:border-[hsl(212_79%_42%_/_0.3)] dark:text-[hsl(207_89%_68%)] mb-4">
          <Info className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">Multiply by days on task is {record.multiplyByDays ? "enabled" : "disabled"}</span>
        </div>

        <div className="space-y-4">
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-baseline gap-2">
                <Calendar className="h-5 w-5 text-[hsl(212_79%_42%)] dark:text-[hsl(207_89%_68%)]" />
                <h3 className="text-xl font-semibold">Schedule & Rewards</h3>
              </div>
              <div className="flex flex-row gap-1.5 items-baseline">
                <h6 className="text-2xl font-bold">{pointRecord?.points || record.points}</h6>
                <p className="text-sm text-muted-foreground m-0">{pluralize((pointRecord?.points || record.points || 0), "Point", "s")} Awarded</p>
              </div>
            </div>

            <div className="rounded-lg border bg-card p-6">
              <div className="grid grid-cols-[repeat(4,auto)] gap-6">
                <div>
                  <p className="text-sm text-muted-foreground m-0 mb-1">Start Date</p>
                  <p className="text-lg font-semibold m-0">{formatDate(record.startDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground m-0 mb-1">End Date</p>
                  <p className="text-lg font-semibold m-0">{record.endDate ? formatDate(record.endDate) : "Not set"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground m-0 mb-1">Duration</p>
                  <p className="text-lg font-semibold m-0">{getDurationText(record)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground m-0 mb-3">Status</p>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={isActive}
                      onCheckedChange={(checked) => {
                        if (isInactive) return;
                        record.set("status", checked ? OUTCOME_INCENTIVE_STATUS.ACTIVE : OUTCOME_INCENTIVE_STATUS.PAUSED);
                      }}
                      disabled={!hasPermission || isInactive}
                      className={cn("disabled:opacity-40", isActive ? "data-[state=checked]:bg-green-500" : isPaused ? "data-[state=unchecked]:bg-yellow-500" : "data-[state=unchecked]:bg-red-500")}
                    />
                    <span
                      className={`text-sm font-medium flex items-center gap-1.5 ${
                        isActive ? "text-green-700 dark:text-green-400" : isPaused ? "text-yellow-700 dark:text-yellow-400" : "text-red-700 dark:text-red-400"
                      }`}
                    >
                      {isActive ? <>Active</> : isPaused ? <>Paused</> : <>Inactive</>}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <div className="flex items-baseline gap-2 mb-4">
              <List className="h-5 w-5 text-[hsl(212_79%_42%)] dark:text-[hsl(207_89%_68%)]" />
              <h3 className="text-xl font-semibold">Filters</h3>
            </div>

            <div className="rounded-lg border bg-card p-6">{renderConditions(record, pointRecord, project)}</div>
          </section>
        </div>

        <div className="mt-6 text-sm text-muted-foreground">
          Created by {record.creatorUserName || "N/A"} on {formatDate(record.createdAt)}
        </div>
      </BaseModalBody>
    </BaseModal>
  );
}
